import { createFileRoute } from "@tanstack/react-router";
import { format } from "date-fns";
import { KeyRound, Loader2, RefreshCw, RotateCw } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { formatApiError } from "@/lib/api";
import { useCurrentUser } from "@/lib/hooks/auth";
import { useKekStatus, useRotateKek } from "@/lib/hooks/admin";
import { useResetMfaErrorOnVerify } from "@/lib/hooks/mfa-verify-prompt";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/settings_/system/security")({
  component: SecurityPage,
});

function SecurityPage(): JSX.Element {
  const { t } = useTranslation();
  const me = useCurrentUser();
  const query = useKekStatus();
  const rotate = useRotateKek();
  useResetMfaErrorOnVerify(rotate);
  const [confirming, setConfirming] = useState(false);

  if (me.data && me.data.role !== "superuser") {
    return (
      <div className="space-y-3">
        <h1 className="text-3xl font-semibold tracking-tight">{t("kek.title")}</h1>
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          {t("kek.superuser_only")}
        </div>
      </div>
    );
  }

  const cfg = query.data;
  const ageWarn = cfg?.age_days != null && cfg.age_days >= cfg.threshold_days;

  async function onRotate(): Promise<void> {
    try {
      await rotate.mutateAsync();
      setConfirming(false);
    } catch {
      // surfaced via rotate.error
    }
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">{t("kek.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("kek.subtitle")}</p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void query.refetch()}
          disabled={query.isFetching}
        >
          <RefreshCw className={cn("mr-1.5 size-3.5", query.isFetching && "animate-spin")} aria-hidden="true" />
          {t("kek.refresh")}
        </Button>
      </header>

      {query.error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          {t("kek.load_error")} — {query.error.message}
        </div>
      )}

      {cfg && (
        <section className="space-y-3 rounded-md border border-border bg-card p-4">
          <header className="flex items-center gap-2">
            <KeyRound className="size-5 text-primary" aria-hidden="true" />
            <h2 className="text-lg font-semibold">{t("kek.card_title")}</h2>
          </header>

          <dl className="grid grid-cols-1 gap-2 text-xs">
            <Row label={t("kek.alias")} value={cfg.alias} mono />
            <Row
              label={t("kek.latest_version")}
              value={cfg.latest_version != null ? String(cfg.latest_version) : "—"}
            />
            <Row
              label={t("kek.min_decryption_version")}
              value={cfg.min_decryption_version != null ? String(cfg.min_decryption_version) : "—"}
            />
            <Row
              label={t("kek.age")}
              value={cfg.age_days != null ? t("kek.age_days", { n: cfg.age_days }) : "—"}
              tone={ageWarn ? "warn" : undefined}
            />
            <Row label={t("kek.threshold")} value={t("kek.age_days", { n: cfg.threshold_days })} />
            <Row
              label={t("kek.auto_rotate")}
              value={cfg.auto_rotate_enabled ? t("kek.on") : t("kek.off")}
              tone={cfg.auto_rotate_enabled ? "ok" : "muted"}
            />
          </dl>

          {cfg.last_rotation && (
            <div className="border-t border-border pt-2">
              <h3 className="text-xs font-semibold text-muted-foreground">{t("kek.last_rotation")}</h3>
              <dl className="mt-1 grid grid-cols-1 gap-1 text-xs">
                <Row
                  label={t("kek.rotated_at")}
                  value={format(new Date(cfg.last_rotation.rotated_at), "PPpp")}
                />
                <Row
                  label={t("kek.rewrapped")}
                  value={`${cfg.last_rotation.dek_rewrapped} / ${cfg.last_rotation.dek_total}`}
                />
                <Row
                  label={t("kek.settle")}
                  value={
                    cfg.last_rotation.min_decryption_version_trimmed
                      ? t("kek.settled")
                      : cfg.last_rotation.settle_until
                        ? t("kek.settling_until", {
                            at: format(new Date(cfg.last_rotation.settle_until), "PP"),
                          })
                        : "—"
                  }
                  tone={cfg.last_rotation.min_decryption_version_trimmed ? "ok" : undefined}
                />
              </dl>
            </div>
          )}

          <div className="rounded-md border border-warning/30 bg-warning/5 p-2 text-[11px] text-muted-foreground">
            {t("kek.rotate_note")}
          </div>

          {rotate.error != null && (
            <ul className="space-y-0.5 text-xs text-destructive" role="alert">
              {formatApiError(rotate.error).map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
          {rotate.data?.rotation_enqueued && !rotate.error && (
            <p className="text-xs text-success">{t("kek.enqueued")}</p>
          )}

          <div className="flex justify-end gap-2">
            {confirming ? (
              <>
                <Button type="button" variant="ghost" size="sm" onClick={() => setConfirming(false)} disabled={rotate.isPending}>
                  {t("common.cancel")}
                </Button>
                <Button type="button" variant="destructive" size="sm" onClick={() => void onRotate()} disabled={rotate.isPending} className="gap-1.5">
                  {rotate.isPending ? (
                    <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
                  ) : (
                    <RotateCw className="size-3.5" aria-hidden="true" />
                  )}
                  {t("kek.confirm_rotate")}
                </Button>
              </>
            ) : (
              <Button type="button" size="sm" onClick={() => setConfirming(true)} className="gap-1.5">
                <RotateCw className="size-3.5" aria-hidden="true" />
                {t("kek.rotate_now")}
              </Button>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

function Row({
  label,
  value,
  mono,
  tone,
}: {
  label: string;
  value: string;
  mono?: boolean;
  tone?: "ok" | "warn" | "muted";
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd
        className={cn(
          "break-all text-right",
          mono && "font-mono",
          tone === "ok" && "text-success",
          tone === "warn" && "text-warning",
          tone === "muted" && "text-muted-foreground",
        )}
      >
        {value}
      </dd>
    </div>
  );
}
